import CustomDatePicker from '@/components/CustomDatePicker';
import { isContractActive } from '@/utils/contractUtils';
import IncomeTableBooking from './IncomeTableBooking';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useIncomeByBookingId } from '@/hooks/admin/shared/useIncomeByBookingId';
import { useDeleteIncome } from '@/hooks/admin/shared/useIncomeMutations';
import {
  Booking,
  BookingFormData,
  useCreateBooking,
  useSearchGuests,
  useUpdateBooking,
} from '@/hooks/useBookings';
import { useContracts } from '@/hooks/useContracts';
import { useDebounce } from '@/hooks/useDebounce';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useProperties } from '@/hooks/useProperties';
import { formatDateForUrl, parseDateFromDatabase } from '@/utils/dateParser';
import { Search, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import IncomeFormModal from './IncomeFormModal';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';

interface BookingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking?: Booking | null;
  preselectedPropertyId?: string;
  preselectedCheckInDate?: Date | null;
  onBookingCreated?: () => void;
  onRequestGuestCreation?: (guestName: string, originId?: string) => void;
  origins?: any[];
}

const BookingFormModal = ({
  isOpen,
  onClose,
  booking,
  preselectedPropertyId,
  preselectedCheckInDate,
  onBookingCreated,
  onRequestGuestCreation,
  origins = [],
}: BookingFormModalProps) => {
  const [checkInDate, setCheckInDate] = useState<Date>();
  const [checkOutDate, setCheckOutDate] = useState<Date>();
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  const [selectedGuest, setSelectedGuest] = useState<any>(null);
  const [showGuestResults, setShowGuestResults] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [selectedOriginId, setSelectedOriginId] = useState('');
  const [stayCompleted, setStayCompleted] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [incomeDefaultValues, setIncomeDefaultValues] = useState<any>(null);
  const [editingIncome, setEditingIncome] = useState<any>(null);

  const guestSearchRef = useRef<HTMLDivElement>(null);
  const debouncedSearchTerm = useDebounce(guestSearchTerm, 500);
  const { data: guestResults = [] } = useSearchGuests(debouncedSearchTerm);
  const { data: properties = [] } = useProperties();
  const { data: contracts = [] } = useContracts();
  const { data: organizations = [] } = useOrganizations();
  const { data: bookingIncomes = [], isLoading: isLoadingIncomes } = useIncomeByBookingId(booking?.booking_id || '');
  const createBookingMutation = useCreateBooking();
  const updateBookingMutation = useUpdateBooking();
  const deleteIncomeMutation = useDeleteIncome();

  const form = useForm<BookingFormData>();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = form;

  const isEditing = !!booking;
  const currentMutation = isEditing
    ? updateBookingMutation
    : createBookingMutation;


  const clearForm = () => {
    reset({
      property_id: '',
      origin_id: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      guest_id: undefined,
      total_guests: undefined,
      children: 0,
      total_payment: undefined,
      cleaning_fee: undefined,
      base_payment: undefined,
      pets_fee: undefined,
      early_checkin_fee: undefined,
      late_checkout_fee: undefined,
      other_fees: undefined,
      estimated_payout: undefined,
      stay_completed: false,
      note: '',
    });

    setCheckInDate(undefined);
    setCheckOutDate(undefined);
    setGuestSearchTerm('');
    setSelectedGuest(null);
    setSelectedPropertyId('');
    setSelectedOriginId('');
    setStayCompleted(false);
    setShowGuestResults(false);
    setShowIncomeModal(false);
    setIncomeDefaultValues(null);
    setEditingIncome(null);
  };

  useEffect(() => {
    if (isOpen) {
      if (booking) {
        const checkInFromDB = parseDateFromDatabase(booking.check_in);
        const checkOutFromDB = parseDateFromDatabase(booking.check_out);

        if (checkInFromDB) setCheckInDate(checkInFromDB);
        if (checkOutFromDB) setCheckOutDate(checkOutFromDB);

        setGuestSearchTerm(booking.guest_name);
        setSelectedGuest({
          id: booking.guest_id,
          name: booking.guest_name,
          email: booking.guest_email,
          phone: booking.guest_phone,
        });

        setSelectedPropertyId(booking.property_id);
        const originId = booking.origin_id || '';
        setSelectedOriginId(originId);
        setStayCompleted(booking.stay_completed || false);

        setValue('property_id', booking.property_id);
        setValue('origin_id', originId);
        setValue('guest_name', booking.guest_name);
        setValue('guest_email', booking.guest_email || '');
        setValue('guest_phone', booking.guest_phone || '');
        setValue('total_guests', booking.total_guests);
        setValue('children', booking.children || 0);
        setValue('total_payment', booking.total_payment);
        setValue('cleaning_fee', booking.cleaning_fee || 0);
        setValue('base_payment', booking.base_payment || 0);
        setValue('pets_fee', booking.pets_fee || 0);
        setValue('early_checkin_fee', booking.early_checkin_fee || 0);
        setValue('late_checkout_fee', booking.late_checkout_fee || 0);
        setValue('other_fees', booking.other_fees || 0);
        setValue('estimated_payout', booking.estimated_payout || 0);
        setValue('stay_completed', booking.stay_completed || false);
        setValue('note', booking.note || '');
      } else {
        clearForm();

        if (preselectedPropertyId) {
          setSelectedPropertyId(preselectedPropertyId);
          setValue('property_id', preselectedPropertyId);
        }

        if (preselectedCheckInDate) {
          setCheckInDate(preselectedCheckInDate);
        }
      }
    }
  }, [
    booking,
    setValue,
    reset,
    isOpen,
    preselectedPropertyId,
    preselectedCheckInDate,
  ]);

  useEffect(() => {
    if (!isOpen) {
      clearForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!showGuestResults) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (guestSearchRef.current && !guestSearchRef.current.contains(e.target as Node)) {
        setShowGuestResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGuestResults]);

  useEffect(() => {
    if (selectedGuest) {
      setValue('guest_name', selectedGuest.name);
      setValue('guest_email', selectedGuest.email || '');
      setValue('guest_phone', selectedGuest.phone || '');
      setValue('guest_id', selectedGuest.id);

      if (!booking && selectedGuest.origin_id) {
        setSelectedOriginId(selectedGuest.origin_id);
        setValue('origin_id', selectedGuest.origin_id);
      }
    }
  }, [selectedGuest, booking]);

  const handleGuestSelect = (guest: any) => {
    setSelectedGuest(guest);
    setGuestSearchTerm(guest.name);
    setValue('guest_name', guest.name);
    setValue('guest_email', guest.email || '');
    setValue('guest_phone', guest.phone || '');
    setValue('guest_id', guest.id);
    setShowGuestResults(false);
  };

  const handleGuestSearchChange = (value: string) => {
    setGuestSearchTerm(value);
    setValue('guest_name', value);
    if (value !== selectedGuest?.name) {
      setSelectedGuest(null);
      setValue('guest_id', undefined);
      setValue('guest_email', '');
      setValue('guest_phone', '');
    }
    setShowGuestResults(true);
  };

  const handleCheckInChange = (date: Date | undefined) => {
    setCheckInDate(date);
    if (date && checkOutDate && checkOutDate <= date) {
      setCheckOutDate(undefined);
    }
  };

  const handleCheckOutChange = (date: Date | undefined) => {
    setCheckOutDate(date);
  };

  const handlePropertyChange = (value: string) => {
    setSelectedPropertyId(value);
    setValue('property_id', value);
  };

  const handleOriginChange = (value: string) => {
    setSelectedOriginId(value);
    setValue('origin_id', value);
  };

  const handleStayCompletedChange = (checked: boolean) => {
    setStayCompleted(checked);
    setValue('stay_completed', checked);
  };

  const handleGenerateIncome = () => {
    if (!booking) return;

    const origin = origins.find((o) => o.id === booking.origin_id);
    const basePayment = booking.base_payment || 0;
    const cleaningFee = booking.cleaning_fee || 0;
    const petsFee = booking.pets_fee || 0;

    const contract = contracts.find((c) => c.property_id === booking.property_id && isContractActive(c));
    const organization = contract?.organization_id
      ? organizations.find((o) => o.id === contract.organization_id)
      : null;
    const isPersonaFisica = organization?.type === 'persona_fisica';

    let defaultValues: any = {
      booking_id: booking.booking_id,
      income_type: 'Hospedaje',
      payment_type: 'Transferencia',
      gross_payment: basePayment,
      fiscal_invoice: false,
      payout_date: new Date(),
    };

    if (origin?.name?.toLowerCase().includes('airbnb')) {
      const grossPayment = isPersonaFisica
        ? basePayment + cleaningFee
        : basePayment + cleaningFee + petsFee;
      const iva = Math.round(grossPayment * 0.16 * 100) / 100;

      const retIva = isPersonaFisica
        ? Math.round(grossPayment * 0.08 * 100) / 100
        : 0;
      const retIsr = isPersonaFisica
        ? Math.round(grossPayment * 0.04 * 100) / 100
        : 0;

      const commissionRate = isPersonaFisica ? 0.1798 : 0.0348;
      const comission = Math.round(grossPayment * commissionRate * 100) / 100;

      const payout =
        Math.round((grossPayment + iva - retIva - retIsr - comission) * 100) /
        100;

      defaultValues = {
        ...defaultValues,
        gross_payment: grossPayment,
        iva,
        ret_iva: retIva,
        ret_isr: retIsr,
        comission,
        payout,
      };
    }

    setIncomeDefaultValues(defaultValues);
    setShowIncomeModal(true);
  };

  const hasHospedajeIncome =
    booking &&
    bookingIncomes.some(
      (income) => income.income_type === 'Hospedaje',
    );

  const handleNewIncome = () => {
    if (!booking) return;

    const defaultValues = {
      booking_id: booking.booking_id,
    };

    setEditingIncome(null);
    setIncomeDefaultValues(defaultValues);
    setShowIncomeModal(true);
  };

  const handleEditIncome = (income: any) => {
    setEditingIncome(income);
    setIncomeDefaultValues({
      ...income,
      payout_date: new Date(income.payout_date),
    });
    setShowIncomeModal(true);
  };

  const handleDeleteIncome = async (incomeId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este ingreso?')) {
      return;
    }

    deleteIncomeMutation.mutate(incomeId, {
      onSuccess: () => {
        if (onBookingCreated) {
          onBookingCreated();
        }
      },
    });
  };

  const onSubmit = (data: BookingFormData) => {
    if (!checkInDate || !checkOutDate) {
      alert('Por favor selecciona las fechas de llegada y salida');
      return;
    }

    if (!selectedOriginId) {
      alert('Por favor selecciona el origen de la reservación');
      return;
    }

    const formData: BookingFormData = {
      ...data,
      check_in: formatDateForUrl(checkInDate),
      check_out: formatDateForUrl(checkOutDate),
      guest_id: selectedGuest?.id,
      stay_completed: stayCompleted,
      origin_id: selectedOriginId,
    };

    if (isEditing && booking) {
      updateBookingMutation.mutate(
        { id: booking.id, bookingData: formData },
        {
          onSuccess: () => {
            if (onBookingCreated) {
              onBookingCreated();
            }
            onClose();
          },
        },
      );
    } else {
      createBookingMutation.mutate(formData, {
        onSuccess: () => {
          if (onBookingCreated) {
            onBookingCreated();
          }
          onClose();
        },
      });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      confirmBeforeClose
      title={
        <div className="flex items-center justify-between w-full">
          <span>{booking ? 'Editar Reservación' : 'Nueva Reservación'}</span>
          {booking && (
            <Badge
              className={
                booking.payment_status === 'paid'
                  ? 'bg-green-100 hover:bg-green-200 text-green-800 ml-4'
                  : booking.payment_status === 'partial'
                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-800 ml-4'
                    : 'bg-red-100 hover:bg-red-200 text-red-800 ml-4'
              }
            >
              {booking.payment_status === 'paid'
                ? 'Pagado'
                : booking.payment_status === 'partial'
                  ? 'Pago parcial'
                  : 'Pendiente de pago'}
            </Badge>
          )}
        </div>
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel={currentMutation.isPending ? 'Guardando...' : booking ? 'Actualizar' : 'Crear Reservación'}
      isLoading={currentMutation.isPending}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-6">
        {/* Propiedad */}
        <div className="space-y-2">
          <Label htmlFor="property_id">Propiedad *</Label>
          <Select
            value={selectedPropertyId}
            onValueChange={handlePropertyChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona una propiedad" />
            </SelectTrigger>

            <SelectContent>
              {properties.map((property) => (
                <SelectItem key={property.id} value={property.id}>
                  {property.shortName || property.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {errors.property_id && (
            <p className="text-red-500 text-sm">Este campo es requerido</p>
          )}
        </div>

        {/* Origen */}
        <div className="space-y-2">
          <Label htmlFor="origin_id">Origen *</Label>

          <Select
            value={selectedOriginId}
            onValueChange={handleOriginChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona el origen" />
            </SelectTrigger>

            <SelectContent>
              {origins.map((origin) => (
                <SelectItem key={origin.id} value={origin.id}>
                  {origin.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {errors.origin_id && (
            <p className="text-red-500 text-sm">Este campo es requerido</p>
          )}
        </div>

        {/* Fechas */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Llegada *</Label>

            <CustomDatePicker
              selected={checkInDate}
              onSelect={handleCheckInChange}
              placeholder="Seleccionar fecha"
              title="Fecha de entrada"
              isAdminContext={true}
            />
          </div>

          <div className="space-y-2">
            <Label>Salida *</Label>

            <CustomDatePicker
              selected={checkOutDate}
              onSelect={handleCheckOutChange}
              placeholder="Seleccionar fecha"
              title="Fecha de salida"
              isAdminContext={true}
            />
          </div>
        </div>

        {/* Búsqueda de Huésped */}
        <div className="space-y-2 relative" ref={guestSearchRef}>
          <Label htmlFor="guest_name">Huésped *</Label>

          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              {...register('guest_name', { required: true })}
              value={guestSearchTerm}
              onChange={(e) => handleGuestSearchChange(e.target.value)}
              placeholder="Buscar huésped por nombre..."
              className="pl-10"
            />
          </div>

          {/* Resultados de búsqueda */}
          {showGuestResults &&
            guestResults.length > 0 &&
            guestSearchTerm.length >= 2 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {guestResults.map((guest) => (
                  <button
                    key={guest.id}
                    type="button"
                    onClick={() => handleGuestSelect(guest)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <div className="font-medium">{guest.name}</div>
                    {guest.email && (
                      <div className="text-sm text-gray-500">
                        {guest.email}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

          {guestResults.length === 0 && guestSearchTerm.length >= 2 && (
            <div className="mt-2 px-4 py-2 text-sm text-muted-foreground">
              No se encontró ningún huésped.
              <Button
                type="button"
                variant="link"
                className="ml-2 text-blue-600 p-0 text-sm"
                onClick={() => onRequestGuestCreation?.(guestSearchTerm, selectedOriginId)}
              >
                Crear nuevo huésped
              </Button>
            </div>
          )}

          {errors.guest_name && (
            <p className="text-red-500 text-sm">Este campo es requerido</p>
          )}
        </div>

        {/* Email y Teléfono */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="guest_email">Email</Label>
            <Input
              {...register('guest_email')}
              type="email"
              placeholder="correo@ejemplo.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="guest_phone">Teléfono</Label>
            <PhoneInput
              value={watch('guest_phone')}
              onChange={(value) => setValue('guest_phone', value)}
              placeholder="+52 123 456 7890"
            />
          </div>
        </div>

        {/* Huéspedes y Niños */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="total_guests">Huéspedes Totales *</Label>
            <div className="relative">
              <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                {...register('total_guests', { required: true, min: 1 })}
                type="number"
                min="1"
                placeholder="2"
                className="pl-10"
              />
            </div>

            {errors.total_guests && (
              <p className="text-red-500 text-sm">Mínimo 1 huésped</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="children">Niños</Label>
            <Input
              {...register('children')}
              type="number"
              min="0"
              placeholder="0"
            />
          </div>
        </div>

        {/* Checkbox de Hospedaje Realizado */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="stay_completed"
            checked={stayCompleted}
            onCheckedChange={handleStayCompletedChange}
          />

          <Label
            htmlFor="stay_completed"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Hospedaje realizado
          </Label>
        </div>

        {/* Precio */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="total_payment">Pago Total *</Label>
            <Input
              {...register('total_payment', { required: true, min: 0 })}
              type="number"
              step="0.01"
              placeholder="0.00"
            />

            {errors.total_payment && (
              <p className="text-red-500 text-sm">
                Este campo es requerido
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="estimated_payout">Pago estimado</Label>
            <Input
              {...register('estimated_payout')}
              type="number"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="base_payment">Precio por Noches</Label>
            <Input
              {...register('base_payment')}
              type="number"
              step="0.01"
              placeholder="0.00"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cleaning_fee">Tarifa de Limpieza</Label>
            <Input
              {...register('cleaning_fee')}
              type="number"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="pets_fee">Tarifa de Mascotas</Label>
            <Input
              {...register('pets_fee')}
              type="number"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Sección de Ingresos */}
        {booking && (
          <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Ingresos</h3>
              {(() => {
                return bookingIncomes.length === 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateIncome}
                    className="text-green-600 border-green-600 hover:bg-green-50"
                  >
                    Generar Ingreso
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleNewIncome}
                    className="text-brand-primary border-brand-primary hover:bg-blue-50"
                  >
                    Nuevo Ingreso
                  </Button>
                );
              })()}
            </div>

            <IncomeTableBooking
              bookingId={booking.booking_id}
              onEditIncome={handleEditIncome}
              onDeleteIncome={handleDeleteIncome}
            />
          </div>
        )}

        {/* Campo de Notas */}
        <div className="space-y-2">
          <Label htmlFor="note">Notas</Label>
          <Textarea
            {...register('note')}
            placeholder="Agregar notas sobre la reservación..."
            className="min-h-[80px]"
          />
        </div>

        <IncomeFormModal
          isOpen={showIncomeModal}
          onClose={() => {
            setShowIncomeModal(false);
            setTimeout(() => {
              setIncomeDefaultValues(null);
              setEditingIncome(null);
            }, 100);
          }}
          defaultValues={incomeDefaultValues}
          editingIncome={editingIncome}
        />
      </div>
    </FormModal>
  );
};

export default BookingFormModal;
